import {
  DeployStatus,
  IDeployBuildWithNo,
  IDeployJobWithNo
} from '@dadohr/shared/domainTypes/deployment';
import { toDoc } from '@dadohr/shared/domainTypes/document';
import { FS } from '@dadohr/shared/versions';
import firebase from 'firebase/app';
import { useCollection, useDocument } from 'react-firebase-hooks/firestore';
import { store, useMappedLoadingValue } from '../../../services/firebase';

export const toBuildDoc = (
  d:
    | firebase.firestore.QueryDocumentSnapshot
    | firebase.firestore.DocumentSnapshot
) => toDoc<IDeployBuildWithNo>(d);

export const toJobDoc = (
  d:
    | firebase.firestore.QueryDocumentSnapshot
    | firebase.firestore.DocumentSnapshot
) => toDoc<IDeployJobWithNo>(d);

export const useBuild = (buildId: string) => {
  return useMappedLoadingValue(
    useDocument(store().collection(FS._deploymentBuilds).doc(buildId)),
    (s) => (s.exists ? toBuildDoc(s) : null)
  );
};

export const useJob = (jobId: string) => {
  return useMappedLoadingValue(
    useDocument(store().collection(FS._deploymentJobs).doc(jobId)),
    (s) => (s.exists ? toJobDoc(s) : null)
  );
};

export const useServiceHistory = (env: string, name: string, limit: number) => {
  return useMappedLoadingValue(
    useCollection(
      store()
        .collection(FS._deploymentJobs)
        .where('env', '==', env)
        .where('name', '==', name)
        .orderBy('startedAt', 'desc')
        .limit(limit)
    ),
    (s) => s.docs.map(toJobDoc)
  );
};

export const SERVICES = [
  'App',
  'App - Staging (prod only)',
  'App - 01 (dev only)',
  'App - 02 (dev only)',
  'App - 03 (dev only)',
  'App - 04 (dev only)',
  'App - 05 (dev only)',
  'CF - admin',
  'CF - adminSchedules',
  'CF - adminUser',
  'CF - automationSchedules',
  'CF - cloudStorage',
  'CF - dataMigrations',
  'CF - enrollmentForms',
  'CF - events',
  'CF - experiences',
  'CF - experiencesQuestionAnswers',
  'CF - externalOperationsWorker',
  'CF - fileSubmission',
  'CF - integrations_adp',
  'CF - integrations_airtable',
  'CF - integrations_asana',
  'CF - integrations_atlassian',
  'CF - integrations_bamboohr',
  'CF - integrations_calendly',
  'CF - integrations_charthop',
  'CF - integrations_docusign',
  'CF - integrations_googleSheets',
  'CF - integrations_lessonly',
  'CF - integrations_microsoft',
  'CF - integrations_okta',
  'CF - integrations_okta_api',
  'CF - integrations_personio',
  'CF - integrations_pingboard',
  'CF - integrations_saml',
  'CF - integrations_sftp',
  'CF - integrations_slack',
  'CF - integrations_swagUp',
  'CF - integrations_twilio',
  'CF - integrations_workday',
  'CF - internalAlerts',
  'CF - mail',
  'CF - moments',
  'CF - oauth',
  'CF - org',
  'CF - playground',
  'CF - postmark',
  'CF - productAnalytics',
  'CF - pubsub',
  'CF - redirect',
  'CF - reports',
  'CF - sampleContent',
  'CF - schedules',
  'CF - sftp',
  'CF - standaloneApp',
  'CF - subscription',
  'CF - user',
  'CF - userImport',
  'CF - deployment',
  'Cloud Function Scheduler',
  'Admin App',
  'Standalone App',
  'Firestore',
  'Realtime Database',
  'Cloud Storage',
  'SSO Routes'
];

export const statusToVariant = (
  status: DeployStatus
): 'success' | 'pending' | 'error' | 'secondary' => {
  if (status === 'SUCCESS') {
    return 'success';
  }
  if (status === 'PENDING') {
    return 'pending';
  }
  if (status === 'ERROR') {
    return 'error';
  }
  return 'secondary';
};

export type DeploymentEnv = 'dev' | 'prod' | 'testing';
export const DEPLOYMENT_ENVS: DeploymentEnv[] = ['dev', 'prod', 'testing'];
